import { useCallback } from 'react'
import WarningIcon from '@mui/icons-material/Warning'
import { Badge, Tooltip } from '@mui/material'
import DOMPurify from 'dompurify'
import size from 'lodash/size'
import * as css from './styles'
import { t } from '@lingui/core/macro'

interface Props {
  errors: any[]
  errorCount: number
  fieldsById: any
  onErrorClick: () => void
}

export const ErrorsTooltip = ({ errors, errorCount, fieldsById, onErrorClick }: Props) => {
  const getTooltipContent = useCallback(() => {
    if (size(fieldsById) > 0 && errors?.length > 0) {
      const errorsHeader = (
        <div key="error-header" css={css.header}>
          {t`Click the error icon to navigate to the following issues`}
        </div>
      )

      const errorsBody = errors.slice(0, 10).map(err => {
        const fieldName = DOMPurify.sanitize(fieldsById[err.field_id]?.field.name, {
          ALLOWED_TAGS: [],
        })
        const isVisible = err.metadata?.isVisible
        const rowNum = err.metadata.row + 1
        const errorMessage = err?.metadata?.comment?.message || err.metadata.validationErrors[0]

        return (
          <div
            key={`error-body-${rowNum}-${err.field_id}`}
            css={css.body}
            dangerouslySetInnerHTML={{
              __html: `${fieldName}${
                isVisible ? '' : ' (hidden)'
              } - Row ${rowNum}: ${errorMessage}`,
            }}
          />
        )
      })

      const errorsFooter =
        errors.length > 10 ? (
          <div key="error-footer" css={css.footer}>
            +{errors.length - 10} {t`more issues`}
          </div>
        ) : null

      return [errorsHeader, ...errorsBody, errorsFooter]
    }

    return 'Quickly navigate to any issues within this submission'
  }, [errorCount, errors, fieldsById])

  const tooltipContent = getTooltipContent()

  return errorCount ? (
    <css.Container onClick={onErrorClick}>
      <Tooltip title={tooltipContent}>
        <Badge
          data-test-id="error-badge"
          css={css.badge}
          badgeContent={errorCount}
          color="error"
          data-test-page-id="error-badge"
        >
          <WarningIcon style={{ opacity: 1 }} />
        </Badge>
      </Tooltip>
    </css.Container>
  ) : (
    <css.Container>
      <Tooltip title={tooltipContent}>
        <Badge
          data-test-id="error-badge-no-errors"
          css={css.badge}
          color="error"
          data-test-page-id="error-badge-no-errors"
        >
          <WarningIcon style={{ opacity: 0.4 }} />
        </Badge>
      </Tooltip>
    </css.Container>
  )
}
